import { useCallback, useEffect, useRef, useState } from 'react'
import { listContacts } from '../api/contacts'

const DEFAULT_LIMIT = 30

function getItems(result) {
  if (Array.isArray(result)) return result
  const items = result?.items || result?.contacts || result?.data || []
  return Array.isArray(items) ? items : []
}

function getTotal(result) {
  const total = result?.total ?? result?.total_count ?? result?.count ?? result?.pagination?.total
  return Number.isFinite(Number(total)) ? Number(total) : null
}

function mergeUnique(previous, incoming) {
  const byId = new Map(previous.map(item => [item.id, item]))
  incoming.forEach(item => byId.set(item.id, item))
  return [...byId.values()]
}

export default function useInfiniteContacts({ tenantId, stage, search, limit = DEFAULT_LIMIT, mapContact }) {
  const [contacts, setContacts] = useState([])
  const [debouncedSearch, setDebouncedSearch] = useState(search.trim())
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState(null)
  const requestIdRef = useRef(0)
  const skipRef = useRef(0)
  const loadingRef = useRef(false)
  const hasMoreRef = useRef(false)

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 350)
    return () => window.clearTimeout(timer)
  }, [search])

  const fetchPage = useCallback(async ({ reset = false } = {}) => {
    if (!tenantId || loadingRef.current || (!reset && !hasMoreRef.current)) return

    const requestId = reset ? ++requestIdRef.current : requestIdRef.current
    const skip = reset ? 0 : skipRef.current
    loadingRef.current = true
    setLoading(true)
    setError(null)

    try {
      const result = await listContacts(tenantId, {
        stage,
        search: debouncedSearch || undefined,
        skip,
        limit,
      })
      if (requestId !== requestIdRef.current) return

      const rawItems = getItems(result)
      const items = mapContact ? rawItems.map(mapContact) : rawItems
      const nextSkip = skip + rawItems.length
      const total = getTotal(result)
      const nextHasMore = total === null ? rawItems.length === limit : nextSkip < total

      skipRef.current = nextSkip
      hasMoreRef.current = nextHasMore
      setHasMore(nextHasMore)
      setTotal(total ?? nextSkip)
      setContacts(previous => reset ? items : mergeUnique(previous, items))
    } catch (nextError) {
      if (requestId !== requestIdRef.current) return
      setError(nextError)
      if (reset) setContacts([])
    } finally {
      if (requestId === requestIdRef.current) {
        loadingRef.current = false
        setLoading(false)
      }
    }
  }, [debouncedSearch, limit, mapContact, stage, tenantId])

  const refresh = useCallback(() => {
    requestIdRef.current += 1
    loadingRef.current = false
    hasMoreRef.current = true
    skipRef.current = 0
    setLoading(false)
    setError(null)
    setTotal(0)
    setContacts([])
    return fetchPage({ reset: true })
  }, [fetchPage])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => () => {
    requestIdRef.current += 1
  }, [])

  const loadMoreRef = useCallback(node => {
    if (!node || loading || !hasMore) return undefined
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) fetchPage()
    }, { rootMargin: '300px 0px' })
    observer.observe(node)
    return () => observer.disconnect()
  }, [fetchPage, hasMore, loading])

  return { contacts, setContacts, loading, hasMore, total, error, refresh, loadMoreRef }
}
